import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Mail, Phone, MapPin, Globe, Hash, Save, ArrowRightCircle, Trash2, Award } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/formatDate";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Converted", "Disqualified"];

export default function LeadDetailSheet({ open, onOpenChange, lead, onUpdated, onConverted, onDeleted }) {
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [converting, setConverting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (lead && open) setEditing({ ...lead });
    }, [lead, open]);

    if (!editing) return null;

    const isConverted = !!editing.converted_broker_id;

    const onSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/leads/${editing.id}`, {
                status: editing.status,
                internal_notes: editing.internal_notes || "",
                assigned_to: editing.assigned_to || "",
            });
            toast.success("Lead updated");
            onUpdated?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const onConvert = async () => {
        setConverting(true);
        try {
            const { data } = await api.post(`/leads/${editing.id}/convert`);
            onConverted?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Convert failed");
        } finally {
            setConverting(false);
        }
    };

    const onDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/leads/${editing.id}`);
            toast.success("Lead deleted");
            setConfirmDelete(false);
            onDeleted?.(editing.id);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    const location = [editing.address1, editing.city, editing.state, editing.zip].filter(Boolean).join(", ");

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-xl overflow-y-auto scrollbar-thin bg-white border-l border-ph-border p-0"
                data-testid="lead-detail-sheet"
            >
                {/* Header */}
                <div className="px-6 pt-6 pb-4 border-b border-ph-border">
                    <SheetHeader>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg bg-ph-primarySoft flex items-center justify-center shrink-0">
                                <Building2 className="w-5 h-5 text-ph-primary" strokeWidth={2} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <SheetTitle className="text-lg font-semibold text-ph-text leading-tight">
                                    {editing.agency_name}
                                </SheetTitle>
                                <SheetDescription className="text-sm text-ph-muted mt-0.5">
                                    {editing.agent_name}{editing.title ? ` · ${editing.title}` : ""}
                                </SheetDescription>
                            </div>
                        </div>
                    </SheetHeader>
                </div>

                <div className="px-6 py-5 space-y-6">
                    {/* Contact card */}
                    <section className="rounded-lg border border-ph-borderSoft p-4 space-y-2.5 bg-ph-canvas/40">
                        <div className="flex items-center gap-2.5 text-sm text-ph-text">
                            <Mail className="w-3.5 h-3.5 text-ph-muted" /> <a className="text-ph-primary hover:underline" href={`mailto:${editing.email}`}>{editing.email}</a>
                        </div>
                        <div className="flex items-center gap-2.5 text-sm text-ph-text">
                            <Phone className="w-3.5 h-3.5 text-ph-muted" /> <a className="text-ph-primary hover:underline" href={`tel:${editing.phone}`}>{editing.phone}</a>
                        </div>
                        {location && (
                            <div className="flex items-start gap-2.5 text-sm text-ph-text">
                                <MapPin className="w-3.5 h-3.5 text-ph-muted mt-0.5" /> <span>{location}</span>
                            </div>
                        )}
                        {editing.website && (
                            <div className="flex items-center gap-2.5 text-sm text-ph-text">
                                <Globe className="w-3.5 h-3.5 text-ph-muted" />
                                <a className="text-ph-primary hover:underline" href={editing.website.startsWith("http") ? editing.website : `https://${editing.website}`} target="_blank" rel="noopener noreferrer">
                                    {editing.website}
                                </a>
                            </div>
                        )}
                        {editing.npn && (
                            <div className="flex items-center gap-2.5 text-sm text-ph-text">
                                <Hash className="w-3.5 h-3.5 text-ph-muted" /> NPN <span className="font-medium">{editing.npn}</span>
                            </div>
                        )}
                    </section>

                    {/* Profile / coverage */}
                    <section>
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-ph-muted mb-3">Coverage profile</h3>
                        <DetailRow label="States licensed" value={editing.states_licensed} />
                        <DetailRow label="Lines" value={(editing.lines || []).join(", ") || "—"} />
                        <DetailRow label="Verticals" value={(editing.verticals || []).join(", ") || "—"} />
                        <DetailRow label="Annual premium" value={editing.premium_band || "—"} />
                        <DetailRow label="Source" value={editing.source || "—"} />
                        <DetailRow label="Submitted" value={fmtDate(editing.submitted_at)} />
                    </section>

                    {editing.notes && (
                        <section>
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-ph-muted mb-2">From applicant</h3>
                            <div className="rounded-lg border border-ph-borderSoft bg-ph-canvas/40 p-3.5 text-sm text-ph-text whitespace-pre-wrap" data-testid="lead-applicant-notes">
                                {editing.notes}
                            </div>
                        </section>
                    )}

                    {/* Triage controls */}
                    <section className="rounded-lg border border-ph-border p-4 space-y-3 bg-white">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-ph-muted">Triage</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="ph-label mb-1 block">Status</label>
                                <Select value={editing.status || "New"} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                                    <SelectTrigger data-testid="lead-status-trigger"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="ph-label mb-1 block">Assigned to</label>
                                <Input
                                    value={editing.assigned_to || ""}
                                    onChange={(e) => setEditing({ ...editing, assigned_to: e.target.value })}
                                    placeholder="e.g. Matt, Jay"
                                    data-testid="lead-assigned-input"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="ph-label mb-1 block">Internal notes</label>
                            <Textarea
                                rows={3}
                                value={editing.internal_notes || ""}
                                onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })}
                                placeholder="Left voicemail at 3pm · Sent intro email Wed…"
                                data-testid="lead-internal-notes"
                            />
                        </div>
                        <Button
                            onClick={onSave}
                            disabled={saving}
                            className="w-full bg-ph-primary text-white font-medium hover:bg-ph-primaryHover"
                            data-testid="lead-save-btn"
                        >
                            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </section>

                    {/* Convert + Delete */}
                    <section className="space-y-3">
                        {isConverted ? (
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 flex items-start gap-3" data-testid="lead-converted-notice">
                                <Award className="w-5 h-5 text-emerald-600 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-ph-text">Already converted to a broker</div>
                                    <p className="text-xs text-ph-muted mt-1">
                                        <a
                                            href={`/app/brokers?broker=${editing.converted_broker_id}`}
                                            className="text-ph-primary hover:underline"
                                            data-testid="lead-go-to-broker"
                                        >
                                            Open in Broker Tracker →
                                        </a>
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <Button
                                onClick={onConvert}
                                disabled={converting}
                                variant="outline"
                                className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                data-testid="lead-convert-btn"
                            >
                                <ArrowRightCircle className="w-4 h-4 mr-2" />
                                {converting ? "Converting…" : "Convert to broker"}
                            </Button>
                        )}

                        <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-ph-text">Delete this lead</div>
                                <p className="text-xs text-ph-muted mt-0.5">Permanently removes the submission. Doesn't touch any broker already created from it.</p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setConfirmDelete(true)}
                                className="border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 shrink-0"
                                data-testid="lead-delete-btn"
                            >
                                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                            </Button>
                        </div>
                    </section>
                </div>
            </SheetContent>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent data-testid="lead-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the submission for {editing.agency_name}. If you already converted it to a broker, that broker stays in the tracker.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            disabled={deleting}
                            className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                            data-testid="lead-delete-confirm-btn"
                        >
                            {deleting ? "Deleting…" : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Sheet>
    );
}

function DetailRow({ label, value }) {
    return (
        <div className="flex items-start gap-3 py-1.5 text-sm">
            <div className="text-ph-muted w-32 shrink-0">{label}</div>
            <div className="text-ph-text flex-1 break-words">{value || "—"}</div>
        </div>
    );
}

// (Award icon already imported above)
